import type { DrCrItem, InvoiceEntryRow } from '@/pages/components/account/gl-invoice/create-stepper/context';
import { makeEmptyDrCrItem, splitDrCrSides } from '@/pages/components/account/gl-invoice/create-stepper/context';
import { isFxOriginalColumn, stripDupSuffix } from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import type { GlColumnDef } from '@/services/accountService/glInvoiceService';

export type NormalSide = 'DR' | 'CR';

/** 与后端 AccountCompanyGLCategoryServiceImpl.normalSideFromCategoryType 对齐 */
export function normalSideFromCategoryType(type?: string | null): NormalSide | undefined {
    const t = String(type || '').trim().toUpperCase();
    if (t === 'ASSET' || t === 'EXPENSE') return 'DR';
    if (t === 'LIABILITY' || t === 'EQUITY' || t === 'REVENUE') return 'CR';
    return undefined;
}

/** type 未知时按 glCode 前缀推断（与后端 inferNormalSideFromCodePrefix 对齐并覆盖常见叶子码） */
export function inferNormalSideFromGlCode(code?: string | null): NormalSide {
    const upper = stripDupSuffix(code || '').toUpperCase();
    if (
        upper.startsWith('BS-CL-')
        || upper.startsWith('BS-EQ-')
        || upper.startsWith('E-EQ-')
        || upper.startsWith('R-')
        || upper.startsWith('L-')
        || upper === 'L'
        || upper === 'R'
        || upper.startsWith('X')
    ) {
        return 'CR';
    }
    return 'DR';
}

export function resolveNormalSideForGlCode(
    code: string | undefined | null,
    typeByCode?: Map<string, string>,
): NormalSide {
    const base = stripDupSuffix(code || '');
    if (typeByCode) {
        const fromType = normalSideFromCategoryType(
            typeByCode.get(base) || (code ? typeByCode.get(code) : undefined),
        );
        if (fromType) return fromType;
    }
    return inferNormalSideFromGlCode(code);
}

/**
 * DR/CR 下拉：不按借贷方向过滤，两侧均可选全部科目列。
 * 保留函数签名以兼容 StepCInvoiceEntry / GlSheetEditView 调用。
 */
export function filterColumnOptionsBySide<T extends { key: string; label: string }>(
    options: T[],
    _columnMap?: Map<string, { glCode?: string }>,
    _side?: 'dr' | 'cr',
    _typeByCode?: Map<string, string>,
    _keepKey?: string | null,
): T[] {
    return options;
}

/** 解析金额字符串（允许千分位逗号）。空 / 非法 → null。 */
export function parseAmountInput(raw: string | null | undefined): number | null {
    const s = String(raw ?? '').replace(/,/g, '').trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

export function formatAmountInput(n: number): string {
    if (!Number.isFinite(n)) return '';
    // 保留最多 2 位小数，去掉无意义的尾零风格用固定 2 位便于对账
    return (Math.round(n * 100) / 100).toFixed(2);
}

/**
 * 录入约束：DR / CR 界面都显示正数（去掉前导负号）。
 * 保存时由 writeDrCrColumnValues 把 CR 转成负数。
 * 保留未完成输入（空、单独 `.`），避免打断打字。
 */
export function coerceAmountInputBySide(_side: 'dr' | 'cr', raw: string): string {
    return String(raw ?? '').replace(/^\s*-/, '').replace(/^\s*\+/, '');
}

/** 失焦时规范化为两位小数正数（完整数字才改写）。 */
export function normalizeAmountOnBlur(side: 'dr' | 'cr', raw: string): string {
    const coerced = coerceAmountInputBySide(side, raw);
    const n = parseAmountInput(coerced);
    if (n == null) return coerced.trim() === '-' || coerced.trim() === '.' ? '' : coerced;
    return formatAmountInput(Math.abs(n));
}

/** 保存用：CR → 负数，DR → 正数 */
export function signedAmountForSave(side: 'dr' | 'cr' | undefined, raw: string): number | null {
    const n = parseAmountInput(raw);
    if (n == null || n === 0) return n === 0 ? 0 : null;
    const abs = Math.abs(n);
    if (side === 'cr') return -abs;
    if (side === 'dr') return abs;
    // side 未设：保留原符号（兼容）
    return n;
}

export type RowDrCrBalance = {
    drTotal: number;
    crTotal: number;
    /** DR − CR；0 表示平衡 */
    diff: number;
    balanced: boolean;
};

/**
 * 是否应从借贷平衡中排除该行。
 * 外币原币列（有汇率配套的 EUR/USD 等）只作参考，平衡只看 Ex HKD / 本币。
 * 无 columnMap 时，已填 exRate 的行也按原币排除（与 Submit 口径一致）。
 */
function shouldExcludeFromDrCrBalance(
    item: DrCrItem | undefined,
    columnMap?: Map<string, GlColumnDef>,
): boolean {
    if (!item) return true;
    if (item.columnKey && columnMap) {
        if (isFxOriginalColumn(columnMap.get(item.columnKey))) return true;
    } else if (String(item.exRate ?? '').trim()) {
        return true;
    }
    return false;
}

/**
 * 行内 DR/CR 合计（两侧均按绝对值汇总；差额 = DR绝对值 − CR绝对值）。
 * 排除外币原币金额，避免 Rate×原币 与 Ex HKD 重复计入。
 */
export function computeRowDrCrBalance(
    items: DrCrItem[],
    columnMap?: Map<string, GlColumnDef>,
): RowDrCrBalance {
    const { dr, cr } = splitDrCrSides(items);
    let drTotal = 0;
    let crTotal = 0;
    for (const idx of dr) {
        const item = items[idx];
        if (shouldExcludeFromDrCrBalance(item, columnMap)) continue;
        const n = parseAmountInput(item?.amount);
        if (n != null) drTotal += Math.abs(n);
    }
    for (const idx of cr) {
        const item = items[idx];
        if (shouldExcludeFromDrCrBalance(item, columnMap)) continue;
        const n = parseAmountInput(item?.amount);
        if (n != null) crTotal += Math.abs(n);
    }
    const diff = Math.round((drTotal - crTotal) * 100) / 100;
    return {
        drTotal: Math.round(drTotal * 100) / 100,
        crTotal: Math.round(crTotal * 100) / 100,
        diff,
        balanced: Math.abs(diff) < 0.005,
    };
}

/** 有金额且借贷不平衡（空行不算）— Edit「找不平衡」筛选用 */
export function isEditRowUnbalanced(
    items: DrCrItem[],
    columnMap?: Map<string, GlColumnDef>,
): boolean {
    const bal = computeRowDrCrBalance(items, columnMap);
    if (bal.drTotal === 0 && bal.crTotal === 0) return false;
    return !bal.balanced;
}

/**
 * 补平：将差额写入对侧第一个空金额格；若无不空则写入对侧最后一格。
 * UI 金额一律正数（CR 保存时再取负）。
 */
export function balanceRowDrCrItems(
    items: DrCrItem[],
    columnMap?: Map<string, GlColumnDef>,
): DrCrItem[] {
    const bal = computeRowDrCrBalance(items, columnMap);
    if (bal.balanced || (bal.drTotal === 0 && bal.crTotal === 0)) return items;

    const { dr, cr } = splitDrCrSides(items);
    const targetSide: 'dr' | 'cr' = bal.diff > 0 ? 'cr' : 'dr';
    const need = Math.abs(bal.diff);
    const sideIdxs = targetSide === 'dr' ? dr : cr;
    if (sideIdxs.length === 0) return items;

    // 不补到原币行（原币不参与平衡）
    let targetIdx = sideIdxs.find((i) =>
        !shouldExcludeFromDrCrBalance(items[i], columnMap)
        && parseAmountInput(items[i]?.amount) == null);
    if (targetIdx == null) {
        targetIdx = [...sideIdxs].reverse().find((i) =>
            !shouldExcludeFromDrCrBalance(items[i], columnMap));
    }
    if (targetIdx == null) return items;

    const next = items.map((it) => ({ ...it }));
    next[targetIdx] = {
        ...next[targetIdx],
        amount: formatAmountInput(need),
        side: targetSide,
    };
    return next;
}

/**
 * 录入时自动填 CR 金额（改 DR 金额/科目后触发）。
 * - 仅 1 个 CR 行：CR = −DR合计（改 DR 时 CR 跟着变）
 * - 多个 CR：只写入第一个空金额格，不覆盖已填
 * 不在用户编辑 CR 时反写，避免抢输入。
 */
export function autoFillOppositeAmount(
    items: DrCrItem[],
    changedIdx?: number,
    columnMap?: Map<string, GlColumnDef>,
): DrCrItem[] {
    if (changedIdx != null) {
        const changed = items[changedIdx];
        const side = changed?.side
            ?? ((parseAmountInput(changed?.amount) ?? 0) < 0 ? 'cr' : 'dr');
        // 改 DR → 填 CR；仅当在 CR 上选科目且金额仍空时也补一次
        if (side === 'cr' && parseAmountInput(changed?.amount) != null) return items;
        if (side !== 'dr' && side !== 'cr') return items;
    }

    const bal = computeRowDrCrBalance(items, columnMap);
    if (bal.drTotal < 0.005) return items;
    if (bal.balanced) return items;

    const { cr } = splitDrCrSides(items);
    const fillableCr = cr.filter((i) => !shouldExcludeFromDrCrBalance(items[i], columnMap));
    if (fillableCr.length === 0) return items;

    let targetIdx: number | undefined;
    if (fillableCr.length === 1) {
        targetIdx = fillableCr[0];
    } else {
        targetIdx = fillableCr.find((i) => parseAmountInput(items[i]?.amount) == null);
    }
    if (targetIdx == null) return items;

    // 单 CR：整笔等于 DR 合计；多 CR 空格：只补当前差额
    const absolute = fillableCr.length === 1 ? bal.drTotal : Math.abs(bal.diff);
    if (absolute < 0.005) return items;

    const next = items.map((it) => ({ ...it }));
    next[targetIdx] = {
        ...next[targetIdx],
        amount: formatAmountInput(absolute),
        side: 'cr',
    };
    return next;
}

export type DuplicateRowOptions = {
    /** 默认 true：清空金额，保留科目 */
    clearAmounts?: boolean;
    /** 默认 true：沿用源行日期 */
    keepDate?: boolean;
};

/** 复制一行用于快速录入相似分录。凭证号原样复制，不自动 +1。 */
export function duplicateInvoiceEntryRow(
    source: InvoiceEntryRow,
    opts?: DuplicateRowOptions,
): InvoiceEntryRow {
    const clearAmounts = opts?.clearAmounts !== false;
    const keepDate = opts?.keepDate !== false;

    // WHY: annotate DrCrItem[] — map alone widens exRate to required, breaking concat with makeEmptyDrCrItem
    let items: DrCrItem[] = (source.drCrItems || []).map((it) => ({
        ...it,
        amount: clearAmounts ? '' : it.amount,
        exRate: clearAmounts ? '' : (it.exRate ?? ''),
    }));
    const sides = splitDrCrSides(items);
    if (sides.dr.length === 0) items = [...items, makeEmptyDrCrItem('dr')];
    if (sides.cr.length === 0) items = [...items, makeEmptyDrCrItem('cr')];

    return {
        key: `row-${Date.now()}-${Math.random()}`,
        date: keepDate ? (source.date || '') : '',
        description: source.description || '',
        voucherNo: source.voucherNo || '',
        invoiceNo: source.invoiceNo || '',
        invoiceDate: keepDate ? (source.invoiceDate || '') : '',
        chequeNo: source.chequeNo || '',
        details: source.details || '',
        price: '',
        drCrItems: items,
        // 新行无 invoiceId，保存时走新增
    };
}

/**
 * 新空行沿用上一笔分录的科目（金额/汇率清空）。
 * 同类业务连录时只需填日期、摘要、金额。
 */
export function copyDrCrAccountsFromRow(source: InvoiceEntryRow | undefined | null): DrCrItem[] {
    if (!source?.drCrItems?.length) {
        return [makeEmptyDrCrItem('dr'), makeEmptyDrCrItem('cr')];
    }
    const hasAnyAccount = source.drCrItems.some((it) => !!it.columnKey);
    if (!hasAnyAccount) {
        return [makeEmptyDrCrItem('dr'), makeEmptyDrCrItem('cr')];
    }
    let items: DrCrItem[] = source.drCrItems.map((it) => ({
        columnKey: it.columnKey,
        amount: '',
        side: it.side,
        exRate: '',
    }));
    const sides = splitDrCrSides(items);
    if (sides.dr.length === 0) items = [...items, makeEmptyDrCrItem('dr')];
    if (sides.cr.length === 0) items = [...items, makeEmptyDrCrItem('cr')];
    return items;
}

/** 从已有行里找最近一笔已选科目的分录（用于 Add / 选完 CR 自动跳行）。 */
export function findLastRowWithAccounts(rows: InvoiceEntryRow[]): InvoiceEntryRow | undefined {
    for (let i = rows.length - 1; i >= 0; i--) {
        const r = rows[i];
        if ((r?.drCrItems || []).some((it) => !!it.columnKey)) return r;
    }
    return undefined;
}

/**
 * 选/换 CR 科目后是否自动加下一行。
 * 只在当前正在录入的最后一行触发；中间行改科目视为改账。
 * 不看金额：连录时科目常已沿用且金额已填，换 CR 仍应开下一行。
 */
export function shouldAdvanceAfterCrAccountSelect(
    items: DrCrItem[],
    idx: number,
    columnKey: string | null,
    isLastRow = true,
): boolean {
    if (!columnKey || !isLastRow) return false;
    const current = items[idx];
    // 点选同一科目不跳
    if (current?.columnKey && current.columnKey === columnKey) return false;
    const sides = splitDrCrSides(items);
    const allDrFilled = sides.dr.every((i) => !!items[i]?.columnKey);
    const allCrFilled = sides.cr.every((i) => !!(i === idx ? columnKey : items[i]?.columnKey));
    return allDrFilled && allCrFilled;
}

/**
 * 金额失焦后是否加下一行（科目已沿用、只填金额的连录）：
 * - 两侧科目都已齐
 * - 失焦前未平衡，失焦后（含 DR→自动补 CR）变为平衡 → 跳行
 * - 失焦前已平衡 → 视为改账，不跳，避免重复加行
 */
export function shouldAdvanceAfterAmountBlur(
    items: DrCrItem[],
    idx: number,
    nextAmount: string,
    columnMap?: Map<string, GlColumnDef>,
): boolean {
    if (parseAmountInput(nextAmount) == null) return false;

    const sidesBefore = splitDrCrSides(items);
    const allAccountsFilled = [...sidesBefore.dr, ...sidesBefore.cr]
        .every((i) => !!items[i]?.columnKey);
    if (!allAccountsFilled) return false;

    const before = computeRowDrCrBalance(items, columnMap);
    if (before.balanced && before.drTotal > 0) return false;

    const preview = items.map((it, i) => (
        i === idx ? { ...it, amount: nextAmount } : it
    ));
    const withAuto = autoFillOppositeAmount(preview, idx, columnMap);
    const after = computeRowDrCrBalance(withAuto, columnMap);
    return after.balanced && after.drTotal > 0;
}
